import { CircularProgress, InputAdornment, Paper, Popper } from '@mui/material';
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import Swal from 'sweetalert2';
import {errorBigIcon, iconNotifySuccess, toastSuccess} from '../../../assets/icons/icons';
import ValidationErrorBlock from '../../../components/Blocks/ValidationErrorBlock/ValidationErrorBlock';
import IsVisibleButton from '../../../components/Buttons/IsVisibleButton/IsVisibleButton';
import { Button, FormControl, TextField } from '../../../components/StyledComponents';
import {MAX_INPUT_VALUE_LENGTH, PASSWORD_REGEX} from '../../../models/consts';
import { IError, IPasswordsVisiblity, IRecoverPasswordForm, IRecoverPasswordProps } from '../../../models/inner-models';
import { IRecoverPasswordConfirm } from '../../../models/models';
import { confirmPasswordRecover, recoverPasswordCognito } from '../../../store/actions';
import { useAppDispatch } from '../../../store/hooks';
import { decoratePopUpMessage } from '../../../utils/popUpTextDecorator';
import {zxcvbn, zxcvbnOptions} from "@zxcvbn-ts/core";
import * as zxcvbnEnPackage from "@zxcvbn-ts/language-en";
import * as zxcvbnCommonPackage from "@zxcvbn-ts/language-common";

import '../style.scss';
import '../ResetPassword/style.scss';

const RecoverPasswordConfirmation: FunctionComponent<IRecoverPasswordProps> = ({ email }: IRecoverPasswordProps) => {
    const dispatch = useAppDispatch();
    const { t: translate } = useTranslation();
    const navigate = useNavigate();
    const [isProcessing, setProcessing] = useState<boolean>(false);

    const { register, formState: { errors, isValid }, handleSubmit, control } = useForm<IRecoverPasswordForm>({
      mode: 'onChange',
    });
    const [notMatchError, setNotMatchError] = useState<boolean>(true);
    const mainPass = useWatch({ control, name: 'password' });
    const passwordValue = mainPass ?? '';
    const [isPasswordFocused, setIsPasswordFocused] = useState(false);
    const passwordAnchorRef = useRef<HTMLDivElement | null>(null);

    const [visible, setPasswordVisiblity] = useState<IPasswordsVisiblity>({
      password: false
    });

    const options = {
      translations: zxcvbnEnPackage.translations,
      graphs: zxcvbnCommonPackage.adjacencyGraphs,
      dictionary: {
        ...zxcvbnCommonPackage.dictionary,
        ...zxcvbnEnPackage.dictionary,
      },
    }
    zxcvbnOptions.setOptions(options)

    const passwordHelpListClassName = useMemo(() => {
      return passwordValue.length ? 'auth-password-help' : 'auth-password-help validation';
    }, [passwordValue.length]);

    const zxcvbnResult = useMemo(() => {
      return passwordValue.length ? zxcvbn(passwordValue) : null;
    }, [passwordValue]);

    const handleClickShowPassword = (field: keyof IPasswordsVisiblity) => () => {
      setPasswordVisiblity({ ...visible, [field]: !visible[field] });
    };

    useEffect(() => {
      const hasDictionaryWords = Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "dictionary"));
      const hasWeakRepeat = Boolean(
        zxcvbnResult?.sequence?.some(i => i.pattern === "repeat") && zxcvbnResult?.score === 0
      );

      if (PASSWORD_REGEX.test(passwordValue) && !(hasDictionaryWords || hasWeakRepeat)) {
        setNotMatchError(false);
      } else {
        setNotMatchError(true);
      }
    }, [passwordValue, zxcvbnResult]);

    const onResend = async () => {
      try {
        setProcessing(true);
        await dispatch(recoverPasswordCognito({ email })).unwrap();
        Swal.fire({
          title: translate('notifications.titles.success'),
          text: 'A verification code has been sent to your email.',
          imageUrl: iconNotifySuccess,
          confirmButtonText: 'OK',
        });
      } catch (err) {
        const error = err as IError;

        console.log(err);

        return Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          imageUrl: errorBigIcon,
          confirmButtonText: translate('notifications.choices.close'),
        });
      } finally {
        setProcessing(false);
      }
    };

    const onSubmit: SubmitHandler<IRecoverPasswordForm> = async (data) => {
      if (notMatchError) {
        return;
      }

      const recoverPassword: IRecoverPasswordConfirm = {
        email,
        confirmation_code: data.code,
        new_password: data.password,
      };

      try {
        setProcessing(true);
        await dispatch(confirmPasswordRecover(recoverPassword)).unwrap();
        await Swal.fire({
          title: translate('notifications.titles.success'),
          text: 'Your password was changed',
          toast: true,
          position: 'top-end',
          timerProgressBar: true,
          showConfirmButton:false,
          showCloseButton: true,
          imageUrl: toastSuccess,
          timer: 3000,
        });

        navigate('/auth/login');

      } catch (err) {
        const error = err as IError;

        console.log(error);
        Swal.fire({
          title: translate('notifications.titles.error'),
          text: decoratePopUpMessage(error.error as string),
          imageUrl: errorBigIcon,
          confirmButtonText: translate('notifications.choices.close'),
        });
      } finally {
        setProcessing(false);
      }
    };


    return (
      <div id="confirmed-recover-password">
        <form noValidate onSubmit={handleSubmit(onSubmit)}>
          <FormControl className="form-row new-reset-password__recover-row" fullWidth>
            <div className="auth-form__label-row">
              <div className="label-block">{translate('forms.recover_password.verification_code')}</div>
              {!!errors.code?.message && (
                <span className="auth-form__field-error">{errors.code.message}</span>
              )}
            </div>
            <div className="auth-form__input-block">
              <TextField
                className="auth-mui-textfield"
                placeholder={translate('forms.recover_password.verification_code')}
                required
                autoFocus
                autoComplete="off"
                id="verifiction-code"
                {...register('code', {
                  required: translate('forms.common.required') as string,
                })}
                error={!!errors.code}
              />
            </div>
          </FormControl>
          <FormControl className="form-row new-reset-password__recover-row" fullWidth>
            <div className="auth-form__label-row">
              <div className="label-block">{translate('forms.recover_password.new_pass')}</div>
              {!!errors.password?.message && (
                <span className="auth-form__field-error">{errors.password.message}</span>
              )}
            </div>
            <div
              className="auth-form__input-block"
              ref={passwordAnchorRef}
              onFocusCapture={() => setIsPasswordFocused(true)}
              onBlurCapture={(e) => {
                const next = e.relatedTarget as Node | null;
                if (!e.currentTarget.contains(next)) {
                  setIsPasswordFocused(false);
                }
              }}
            >
              <TextField
                className="auth-mui-passfield"
                placeholder={translate('forms.common.password')}
                required
                id="password"
                type={visible.password ? 'text' : 'password'}
                {...register('password', {
                  required: translate('forms.common.required') as string,
                  maxLength: {
                    value: MAX_INPUT_VALUE_LENGTH,
                    message: translate('forms.common.max_length50'),
                  },
                  pattern: {
                    value: PASSWORD_REGEX,
                    message: translate('forms.common.invalid_password'),
                  },
                })}
                error={!!errors.password || (passwordValue.length > 0 && notMatchError)}
                inputProps={{ maxLength: MAX_INPUT_VALUE_LENGTH }}
                InputProps={{
                  endAdornment:
                    <InputAdornment position="end">
                      <IsVisibleButton
                        isPrimary
                        onClick={handleClickShowPassword('password')}
                        isNowVisible={visible.password}
                      />
                    </InputAdornment>,
                }}
              />
              <Popper
                open={isPasswordFocused && passwordValue.length > 0}
                anchorEl={passwordAnchorRef.current}
                placement="top-start"
                modifiers={[
                  { name: 'offset', options: { offset: [0, -12] } },
                ]}
                className="auth-password-rules-popper"
              >
                <Paper elevation={0} className="auth-password-rules-tooltip">
                  <ul className={passwordHelpListClassName}>
                    <li className={passwordValue.length > 9 ? 'success' : 'error'}>
                      10 characters minimum
                    </li>
                    <li className={
                      Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "dictionary")) ||
                      (Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "repeat")) && zxcvbnResult?.score === 0)
                        ? 'error'
                        : 'success'
                    }>
                      No dictionary words, repetitions and commonly used passwords
                    </li>
                    <li className={!/\s/.test(passwordValue) ? 'success' : 'error'}>
                      No spaces
                    </li>
                    <li className={/^(.*[0-9].*)$/.test(passwordValue) ? 'success' : 'error'}>
                      One number
                    </li>
                    <li className={/^(?=.*[a-z])(?=.*[A-Z]).*$/.test(passwordValue) ? 'success' : 'error'}>
                      At least one uppercase and one lowercase
                    </li>
                    <li className={/^(?=.*[\/^$*.\[\]{}()?\-"!@#%&\\,><':;|_~`+=])[a-zA-Z0-9\/^$*.\[\]{}()?\-"!@#%&\\,><':;|_~`+=]+$/.test(mainPass) ? 'success' : 'error'}>
                      One special character.<br/>
                      Use only: {"^ $ * . [ ] { } ( ) ? - \" ! @ # % & / \\ , > < ' : ; | _ ~ ` + ="}
                    </li>
                    {(!PASSWORD_REGEX.test(passwordValue) ||
                      Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "dictionary")) ||
                      (Boolean(zxcvbnResult?.sequence?.some(i => i.pattern === "repeat")) && zxcvbnResult?.score === 0)) && (
                      <li className="error">
                        {translate('forms.common.invalid_password')}
                      </li>
                    )}
                  </ul>
                </Paper>
              </Popper>
            </div>
          </FormControl>
          <div className="form-row">
            <div className="new-reset-password__resend" onClick={onResend}>
              Resend code
            </div>
          </div>
          <div className="action-row">
            <Button className="primary-button w-100" type="submit" disabled={!isValid || notMatchError || isProcessing}>
              {isProcessing &&
                <div className="small-spinner-wrapper">
                  <CircularProgress color="inherit" className="auth-btn-spinner" />
                </div>
              }
              {isProcessing
                ? translate('notifications.choices.processing')
                : translate('forms.recover_password.title')}
            </Button>
          </div>
        </form>
      </div>
    );

  }
;

export default RecoverPasswordConfirmation;
